import { useEffect, useRef, useState } from 'react';
import type { DesktopState } from '@kiipu/desktop-protocol';
import { ArrowRight } from '@phosphor-icons/react';
import { Brand, Button, ErrorNotice } from '@kiipu/ui';
import { desktopBridge, platformState, usePlatform } from '../lib/client-platform';

const idle = { status: 'idle' } as const;
type UnexpectedError = { auth: DesktopState['auth']; message: string };
type Operation = 'begin' | 'reopen' | 'cancel' | 'external';

/** The native process owns the flow; this view only asks it to act and renders its state. */
export function DesktopAuthPage() {
  const platform = usePlatform();
  const auth = platform?.auth ?? idle;
  const [invoking, setInvoking] = useState<Operation | null>(null);
  const [unexpected, setUnexpected] = useState<UnexpectedError | null>(null);
  const [focusRequest, setFocusRequest] = useState(0);
  const operation = useRef(0);
  const invokingRef = useRef<Operation | null>(null);
  const primary = useRef<HTMLButtonElement>(null);
  const focusPending = useRef(false);
  const waiting = auth.status === 'waiting';
  const exchanging = auth.status === 'exchanging';
  const opening = auth.status === 'opening';
  const busy = opening || exchanging || invoking === 'begin';
  const error =
    auth.status === 'error' ? auth.message : unexpected?.auth === auth ? unexpected.message : '';

  useEffect(() => {
    if (auth.status === 'idle' && !invoking && focusPending.current) {
      focusPending.current = false;
      primary.current?.focus();
    }
  }, [auth.status, invoking, focusRequest]);

  async function invoke(kind: Operation, action: () => Promise<void>, fallback: string) {
    if (invokingRef.current && (kind !== 'cancel' || invokingRef.current === 'cancel'))
      return false;
    invokingRef.current = kind;
    const attempt = ++operation.current;
    const before = platformState()?.auth ?? idle;
    setInvoking(kind);
    setUnexpected(null);
    try {
      await action();
      return true;
    } catch {
      // Expected flow errors arrive through native state. A stale IPC rejection must
      // never replace the state or message of a newer native login attempt.
      const current = platformState()?.auth ?? idle;
      if (operation.current === attempt && current === before && current.status !== 'error')
        setUnexpected({ auth: current, message: fallback });
      return false;
    } finally {
      if (operation.current === attempt) {
        invokingRef.current = null;
        setInvoking(null);
      }
    }
  }

  function openAccount(path: '/register' | '/login') {
    void invoke(
      'external',
      async () => {
        const bridge = desktopBridge();
        const origin = platformState()?.apiOrigin;
        if (!bridge || !origin) throw new Error();
        await bridge.openExternal(new URL(path, origin).href);
      },
      '无法打开浏览器，请稍后重试。',
    );
  }

  const label = opening
    ? '正在打开浏览器…'
    : exchanging
      ? '正在完成登录…'
      : waiting
        ? '重新打开浏览器'
        : auth.status === 'error'
          ? '重新登录'
          : '在浏览器中登录';

  return (
    <main className="desktop-auth" aria-labelledby="desktop-auth-title">
      <section className="desktop-auth-content">
        <header className="desktop-auth-heading">
          <div className="desktop-auth-symbol" aria-hidden="true">
            <Brand compact />
          </div>
          <h1 id="desktop-auth-title">欢迎使用 Kiipu</h1>
          <p>收藏值得留下的内容，在桌面上继续阅读与整理。</p>
        </header>

        <div className="desktop-auth-panel">
          {waiting || opening || exchanging ? (
            <p
              className={`desktop-auth-status${waiting ? '' : ' sr-only'}`}
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {waiting ? '等待浏览器授权' : label}
            </p>
          ) : null}
          <ErrorNotice error={error} />
          <Button
            type="button"
            variant="primary"
            className="desktop-auth-primary"
            ref={primary}
            busy={busy}
            disabled={Boolean(invoking)}
            aria-describedby="desktop-auth-instruction"
            onClick={() => {
              void invoke(
                waiting ? 'reopen' : 'begin',
                async () => {
                  const bridge = desktopBridge();
                  if (!bridge) throw new Error();
                  if (waiting) await bridge.reopenSignIn();
                  else await bridge.beginSignIn();
                },
                '暂时无法发起登录，请稍后重试。',
              );
            }}
          >
            {label}
            {!busy ? <ArrowRight size={16} aria-hidden="true" /> : null}
          </Button>
          <p className="desktop-auth-instruction" id="desktop-auth-instruction">
            {waiting
              ? '请在浏览器中确认授权，完成后会自动返回。'
              : exchanging
                ? '正在安全连接你的内容库。'
                : '将在默认浏览器中完成登录，授权后自动返回。'}
          </p>
          {waiting || exchanging ? (
            <button
              type="button"
              className="desktop-auth-cancel"
              disabled={invoking === 'cancel'}
              onClick={() => {
                void invoke(
                  'cancel',
                  async () => {
                    const bridge = desktopBridge();
                    if (!bridge) throw new Error();
                    await bridge.cancelSignIn();
                  },
                  '暂时无法取消登录，请重试。',
                ).then((cancelled) => {
                  if (cancelled) {
                    focusPending.current = true;
                    setFocusRequest((request) => request + 1);
                  }
                });
              }}
            >
              取消登录
            </button>
          ) : null}
        </div>

        <nav className="desktop-auth-links" aria-label="账户帮助">
          <button
            type="button"
            disabled={Boolean(invoking)}
            onClick={() => openAccount('/register')}
          >
            创建账户
          </button>
          <span aria-hidden="true">·</span>
          <button type="button" disabled={Boolean(invoking)} onClick={() => openAccount('/login')}>
            找回密码
          </button>
        </nav>
      </section>
    </main>
  );
}
